import React, { useEffect, useRef, useState } from "react";
import { useCombinedStore } from "../store";
import Modal from "react-modal";
import MessageBox from "./MessageBox";
import api from "../services/api.service";
import * as Styles from "../styles/ReviewOrderTax.Modal.styles";
import alfredoIcon from "../assets/alfredo-icon.svg";

interface ReviewOrderTaxModalProps {
  isOpen: boolean;
  onRequestClose: () => void;
}

const ReviewOrderTaxModal: React.FC<ReviewOrderTaxModalProps> = ({
  isOpen,
  onRequestClose,
}) => {
  const {
    subsidiaryDataZustand,
    setOrderDataZustand,
    orderDataZustand,
    authUserZustand,
    setMessageZustand,
  } = useCombinedStore((state) => ({
    setSubsidiaryDataZustand: state.setSubsidiaryDataZustand,
    subsidiaryDataZustand: state.subsidiaryDataZustand,
    setOrderDataZustand: state.setOrderDataZustand,
    orderDataZustand: state.orderDataZustand,
    setMessageZustand: state.setMessageZustand,
    authUserZustand: state.authUserZustand,
  }));

  const modalWrapperRef = useRef<HTMLDivElement>(null);
  const [selectedTaxs, setSelectedTaxs] = useState<{ taxUuid: string; taxPeople?: string }[]>([]);

  useEffect(() => {
    if (orderDataZustand?.orderInfo?.orderTaxs) {
      const updatedSelectedTaxs = orderDataZustand?.orderInfo?.orderTaxs?.map((tax: any) => ({
        taxUuid: tax.taxUuid,
        taxPeople: tax.taxPeople ? String(tax.taxPeople) : undefined,
      }));
      setSelectedTaxs(updatedSelectedTaxs);
    }
  }, [orderDataZustand]);

  const handleCheckboxChange = (taxUuid: string, taxPeople?: string) => {
    const isSelected = selectedTaxs.some(
      (selectedTax) =>
        selectedTax.taxUuid === taxUuid &&
        (taxPeople ? selectedTax.taxPeople === taxPeople : true)
    );

    if (isSelected) {
      setSelectedTaxs(selectedTaxs.filter(
        (tax) =>
          tax.taxUuid !== taxUuid || (taxPeople && tax.taxPeople !== taxPeople)
      ));
    } else {
      setSelectedTaxs([...selectedTaxs, { taxUuid, taxPeople }]);
    }
  };

  const handleSubmit = async () => {
    try {
      const updatedOrderTaxs = selectedTaxs.reduce((acc: any[], selectedTax) => {
        const exists = acc.some(
          (tax) =>
            tax.taxUuid === selectedTax.taxUuid &&
            (!selectedTax.taxPeople || tax.taxPeople === selectedTax.taxPeople)
        );

        if (!exists) {
          acc.push(selectedTax);
        }

        return acc;
      }, []);

      await api.post(`updateOrderTaxs/${orderDataZustand.orderUuid}`, {
        selectedTaxs: updatedOrderTaxs,
        period: subsidiaryDataZustand.selectedPdv.selectedPeriod.periodType,
      });

      setMessageZustand(`Taxas atualizadas com sucesso!`, `success`);
      onRequestClose();
    } catch (error: any) {
      setMessageZustand(
        `Erro ao atualizar taxas: ${error?.response?.data?.message}`,
        `error`
      );
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onRequestClose}
      style={Styles.customStyles}
      contentLabel="Main Modal"
      ariaHideApp={false}
    >
      <MessageBox />
      <Styles.ModalWrapper
        tabIndex={-1}
        ref={modalWrapperRef}
        onKeyDown={(event) => {
          if (event.key === "Enter") {
            handleSubmit();
          }
        }}
      >
        <Styles.CloseButton onClick={onRequestClose}>&times;</Styles.CloseButton>
        <Styles.ModalContent>
          <Styles.ModalHeader
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Logo src={alfredoIcon} alt="Alfredo Web OPS" />
            <Styles.ModalTitle>Revisar Taxas</Styles.ModalTitle>
            <Styles.ModalDescription>
              Inclua ou exclua taxas aplicadas à conta.
            </Styles.ModalDescription>
          </Styles.ModalHeader>
          <Styles.ModalBody
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .backgroundColor
            }
          >
            <Styles.RowContainer>
              <Styles.TableContainer>
                <Styles.ProductsTable>
                  <thead>
                    <tr>
                      <th style={{ width: "40%" }}>Nome da Taxa</th>
                      <th style={{ width: "25%" }}>Tipo de Taxa</th>
                      <th style={{ width: "25%" }}>Valor da Taxa</th>
                      {orderDataZustand?.orderInfo?.peopleNumber > 1 && (
                        <th style={{ width: "15%" }}>Lugar</th>
                      )}
                      <th style={{ width: "20%" }}>Aplicada?</th>
                    </tr>
                  </thead>
                  <tbody>
                    {subsidiaryDataZustand.selectedPdv.selectedPeriod.periodInfo?.periodTaxs?.Taxs?.map(
                      (tax: any) => (
                        <React.Fragment key={tax.taxUuid}>
                          <Styles.TableRow
                            onClick={() =>
                              handleCheckboxChange(
                                tax.taxUuid,
                                orderDataZustand?.orderInfo?.peopleNumber > 1 ? "1" : undefined
                              )
                            }
                          >
                            <td>{tax.taxName}</td>
                            <td>{tax.taxValue.includes("%") ? "Porcentagem" : "Fixa"}</td>
                            <td>{tax.taxValue}</td>
                            {orderDataZustand?.orderInfo?.peopleNumber > 1 ? (
                              <td colSpan={2}>
                                <table style={{ width: "100%" }}>
                                  <tbody>
                                    {Array.from({ length: Number(orderDataZustand?.orderInfo?.peopleNumber) }).map(
                                      (_, index) => {
                                        const personIndex = (index + 1).toString();
                                        return (
                                          <tr
                                            key={`${tax.taxUuid}-${personIndex}`}
                                            onClick={() => handleCheckboxChange(tax.taxUuid, personIndex)}
                                          >
                                            <td>{`Lugar ${personIndex}`}</td>
                                            <td>
                                              <input
                                                type="checkbox"
                                                checked={selectedTaxs.some(
                                                  (selectedTax) =>
                                                    selectedTax.taxUuid === tax.taxUuid &&
                                                    selectedTax.taxPeople === personIndex
                                                )}
                                                onChange={(event) => {
                                                  event.stopPropagation(); // Impede a propagação para o <tr>
                                                  handleCheckboxChange(tax.taxUuid, personIndex);
                                                }}
                                              />
                                            </td>
                                          </tr>
                                        );
                                      }
                                    )}
                                  </tbody>
                                </table>
                              </td>
                            ) : (
                              <td>
                                <input
                                  type="checkbox"
                                  checked={selectedTaxs.some(
                                    (selectedTax) => selectedTax.taxUuid === tax.taxUuid
                                  )}
                                  onChange={(event) => {
                                    event.stopPropagation(); // Impede a propagação para o <Styles.TableRow>
                                    handleCheckboxChange(tax.taxUuid);
                                  }}
                                />
                              </td>
                            )}
                          </Styles.TableRow>

                        </React.Fragment>
                      )
                    )}
                  </tbody>
                </Styles.ProductsTable>
              </Styles.TableContainer>
            </Styles.RowContainer>
          </Styles.ModalBody>
          <Styles.ModalFooter
            color={
              subsidiaryDataZustand.selectedPdv.homeInfo.itemInfoValue
                .buttonsColor
            }
          >
            <Styles.Button onClick={onRequestClose}>Cancelar</Styles.Button>
            <Styles.Button onClick={handleSubmit}>Atualizar</Styles.Button>
          </Styles.ModalFooter>
        </Styles.ModalContent>
      </Styles.ModalWrapper>
    </Modal>
  );
};

export default ReviewOrderTaxModal;
